// Positive Response interrupt hook.
//
// Subscribes to Supabase Realtime on audit_events filtered by
// owner_id. When a new row lands with payload.detector ==
// 'positive_response', fires a browser notification (with permission)
// and dispatches a custom event so any component can show a toast.
//
// Also handles the initial permission request politely — only asks
// once per session, when the user is authenticated (never on cold
// page load).

import React from 'react';
import { supabase } from '../lib/supabase';

export function usePositiveResponseAlerts({ userId, onAlert } = {}) {
  const askedPermissionRef = React.useRef(false);

  React.useEffect(() => {
    if (!userId) return;

    // Ask for Notification permission once when the user is authed.
    // Only ask if the current state is 'default' — never re-ask if
    // they've denied it.
    try {
      if ('Notification' in window && Notification.permission === 'default' && !askedPermissionRef.current) {
        askedPermissionRef.current = true;
        // Wait 3 seconds so the ask doesn't hit on cold-load.
        const t = setTimeout(() => {
          try { Notification.requestPermission().catch(() => {}); } catch { /* noop */ }
        }, 3000);
        // Note: not cleaning up t; if the user leaves within 3s, no biggie.
      }
    } catch { /* Notification API not supported */ }

    const channel = supabase
      .channel(`positive_response_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_events',
          filter: `owner_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row) return;
          let detector = null;
          try { detector = row.payload?.detector; } catch { detector = null; }
          if (detector !== 'positive_response') return;

          const p = row.payload || {};
          const alert = {
            id: row.id,
            headline: p.headline || '⚡ New positive reply',
            preview: p.preview || '',
            contact_name: p.contact_name || 'A prospect',
            priority: p.priority || 'high',
            suggested_action: p.suggested_action || 'call_now',
            suggested_action_title: p.suggested_action_title || 'Call now',
            received_at: p.received_at || row.created_at,
            client_id: row.client_id,
            lead_id: row.lead_id,
          };

          // Native browser notification if permitted.
          try {
            if ('Notification' in window && Notification.permission === 'granted') {
              const n = new Notification(alert.headline, {
                body: alert.preview,
                icon: '/logo192.png',
                tag: `positive_response_${row.id}`,   // dedup
                requireInteraction: alert.priority === 'critical',
              });
              n.onclick = () => {
                try { window.focus(); } catch { /* noop */ }
                try { n.close(); } catch { /* noop */ }
                if (alert.client_id) {
                  window.dispatchEvent(new CustomEvent('crm-open-client', { detail: { client_id: alert.client_id } }));
                }
              };
            }
          } catch { /* noop */ }

          // In-app custom event so a top-of-page toast can render.
          try {
            window.dispatchEvent(new CustomEvent('crm-positive-response-alert', { detail: alert }));
          } catch { /* noop */ }

          if (typeof onAlert === 'function') onAlert(alert);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, onAlert]);
}

export default usePositiveResponseAlerts;
